'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';

interface CodeForm {
  code: string;
}

export default function HomeCollaborateurDoor() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [codeError, setCodeError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CodeForm>({ defaultValues: { code: '' } });

  const codeValue = watch('code');

  const handleCodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace(/[IO]/g, '');
    setValue('code', raw);
    setCodeError('');
  };

  const onSubmit = async (data: CodeForm) => {
    setIsLoading(true);
    setCodeError('');
    await new Promise((r) => setTimeout(r, 900));
    const mockValid = data.code.length >= 6;
    if (!mockValid) {
      setCodeError('Code invalide ou expiré. Vérifiez le code reçu de votre consultant.');
      setIsLoading(false);
      return;
    }
    router.push(`/diagnostic-questionnaire?code=${encodeURIComponent(data.code)}`);
  };

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden flex flex-col hover:shadow-lg transition-shadow duration-300">
      <div className="accent-gradient p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
            <Icon name="UserCircleIcon" size={22} variant="solid" className="text-white" />
          </div>
          <div>
            <p className="text-amber-100 text-xs font-semibold uppercase tracking-widest">Collaborateur · Équipe</p>
            <h2 className="text-white text-lg font-bold">Participer au diagnostic</h2>
          </div>
        </div>
        <p className="text-amber-50 text-sm leading-relaxed">
          Saisissez le code WB-ORG transmis par votre consultant pour accéder au questionnaire anonyme.
        </p>
      </div>

      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 gap-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Code de campagne
            </label>
            <div className="relative">
              <input
                {...register('code', {
                  required: 'Veuillez saisir votre code de campagne.',
                  minLength: { value: 6, message: 'Code trop court.' },
                })}
                onChange={handleCodeInput}
                placeholder="WB-ORG-A7B2"
                maxLength={11}
                className="w-full font-mono text-base font-semibold tracking-widest bg-input border border-border rounded-xl px-4 py-3.5 pr-12 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-150 uppercase"
                autoComplete="off"
                spellCheck={false}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {codeValue.length >= 6 && !codeError ? (
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
                ) : (
                  <Icon name="KeyIcon" size={18} variant="outline" className="text-muted-foreground" />
                )}
              </div>
            </div>
            {(errors.code || codeError) && (
              <p className="mt-2 text-xs text-danger flex items-center gap-1.5">
                <Icon name="ExclamationCircleIcon" size={14} variant="solid" className="text-danger" />
                {errors.code?.message || codeError}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 bg-accent text-white font-semibold text-sm px-6 py-3.5 rounded-xl hover:bg-accent/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed mt-auto"
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={18} variant="outline" className="text-white animate-spin" />
                Vérification…
              </>
            ) : (
              <>
                <Icon name="ArrowRightCircleIcon" size={18} variant="solid" className="text-white" />
                Accéder au questionnaire
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-muted-foreground text-center mt-4 flex items-center justify-center gap-1.5">
          <Icon name="ShieldCheckIcon" size={13} variant="solid" className="text-success" />
          Réponses 100 % anonymes — aucune donnée personnelle collectée
        </p>
      </div>
    </div>
  );
}